import React, { useEffect, useState } from 'react';
import { 
  FileText, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  CheckCircle2,
  XCircle,
  Download,
  Printer,
  ChevronDown
} from 'lucide-react';
import { generateInvoicePdf } from '../lib/invoicePdf';

import { cn } from '../lib/utils';
import { Toaster } from 'sonner';
import { notify } from '../services/notify';

interface PublicInvoicePortalProps {
  invoiceId: string;
}

export default function PublicInvoicePortal({ invoiceId }: PublicInvoicePortalProps) {
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [brandingTheme, setBrandingTheme] = useState<any>(null);

  useEffect(() => {
    if (!invoiceId) return;

    // Public links must work for clients on any hostname. The public API does
    // not require a session and is the canonical source for local Postgres data.
    let cancelled = false;
    fetch(`/api/public/documents/${encodeURIComponent(invoiceId)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Invoice not found' : 'Error fetching invoice');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.type !== 'invoice') {
          setError('Invalid document type');
          setLoading(false);
          return;
        }
        setInvoice({ ...data, items: Array.isArray(data.items) ? data.items : [] });
        if (data.orgSettings) setOrgSettings(data.orgSettings);
        if (data.brandingThemeData) setBrandingTheme(data.brandingThemeData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Error fetching invoice');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [invoiceId]);

  const calculateItemSubtotal = (item: any) => {
    return (item.quantity * item.unitPrice) * (1 - (item.discount || 0) / 100);
  };

  const calculateItemTax = (item: any) => {
    const subtotal = calculateItemSubtotal(item);
    // Rough estimate or pull from settings, but for simple portal we use a standard rate if not specified
    const rate = parseFloat(String(item?.taxRate || '').replace(/[^0-9.]/g, '') || '0') / 100;
    return subtotal * rate;
  };

  const calculateItemTotal = (item: any) => {
    return calculateItemSubtotal(item) + calculateItemTax(item);
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    try {
      await generateInvoicePdf({
        invoice,
        organization: orgSettings,
        contact: invoice.contact,
        brandingTheme,
        calculateSubtotal: calculateItemSubtotal,
        calculateTax: calculateItemTax,
      });
      notify.confirm('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF Generation Error:', err);
      notify.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E4E3E0] flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-[#141414]/10 border-t-[#141414] rounded-full animate-spin" />
        <p className="font-bold text-[#141414]/40 uppercase tracking-widest text-xs">Loading Invoice...</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-[#E4E3E0] flex flex-col items-center justify-center p-8 text-center">
        <XCircle className="w-16 h-16 text-red-500 mb-4 opacity-50" />
        <h1 className="text-2xl font-bold text-[#141414] italic serif mb-2">Something went wrong</h1>
        <p className="text-[#141414]/60 max-w-md">{error || 'This invoice link might be invalid or expired.'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E4E3E0] text-[#141414] font-sans selection:bg-[#141414] selection:text-[#E4E3E0]">
      <Toaster position="bottom-center" visibleToasts={1} duration={2000} />
      
      {/* Portal Header */}
      <header className="bg-white/50 backdrop-blur-md border-b border-[#141414]/10 sticky top-0 z-50 px-8 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#141414] text-[#E4E3E0] rounded-xl flex items-center justify-center rotate-3 shadow-lg">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Client Portal</p>
              <h1 className="text-lg font-bold italic serif leading-none">Invoice {invoice.number}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => window.print()}
              className="p-2 hover:bg-[#141414]/5 rounded-xl transition-colors text-[#141414]/60 hover:text-[#141414]"
              title="Print Invoice"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button 
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all shadow-lg active:scale-95"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-8 md:p-16 space-y-12">
        {/* Invoice Status Banner */}
        <div className={cn(
          "p-6 rounded-2xl border flex items-center justify-between",
          invoice.status === 'Paid' ? "bg-green-500/10 border-green-200 text-green-700" :
          invoice.status === 'Awaiting Payment' ? "bg-amber-500/10 border-amber-200 text-amber-700" :
          invoice.status === 'Voided' || invoice.status === 'Deleted' ? "bg-red-500/10 border-red-200 text-red-700" :
          "bg-gray-500/10 border-gray-200 text-gray-700"
        )}>
          <div className="flex items-center gap-4">
            {invoice.status === 'Paid' ? <CheckCircle2 className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Status</p>
              <p className="font-bold text-lg italic serif">This invoice is currently {invoice.status.toLowerCase()}.</p>
            </div>
          </div>
          {invoice.status === 'Paid' && invoice.paymentReceived > 0 && (
            <div className="text-right">
               <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Amount Paid</p>
               <p className="font-bold">{invoice.currency} {invoice.paymentReceived.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          )}
        </div>

        {/* Invoice Content Card */}
        <div className="bg-white border border-[#141414]/10 rounded-3xl shadow-2xl overflow-hidden print:border-0 print:shadow-none">
          {/* Company Info */}
          <div className="p-12 bg-white/50 border-b border-[#141414]/5 space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-8">
              <div className="space-y-4">
                {(brandingTheme?.logoOverrideUrl || brandingTheme?.logoUrl || orgSettings?.logoUrl) ? (
                  <img 
                    src={brandingTheme?.logoOverrideUrl || brandingTheme?.logoUrl || orgSettings.logoUrl} 
                    alt="Logo" 
                    className={cn(
                      "object-contain",
                      brandingTheme?.logoSize === 'Small' ? 'h-12' : 
                      brandingTheme?.logoSize === 'Large' ? 'h-32' : 'h-20'
                    )}
                    referrerPolicy="no-referrer" 
                  />
                ) : (
                  <div className="w-16 h-16 bg-[#141414] rounded-2xl flex items-center justify-center font-bold text-2xl text-[#E4E3E0] italic serif">
                    {orgSettings?.name?.substring(0, 1) || 'A'}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold tracking-tight italic serif">{orgSettings?.name || 'Accountex Organization'}</h2>
                  <div className="mt-2 space-y-1 text-xs text-[#141414]/60 font-medium">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> 
                      {brandingTheme?.organizationDetails || orgSettings?.address || 'Company Address'}
                    </p>
                    {orgSettings?.website && <p className="flex items-center gap-2"><Globe className="w-3 h-3" /> {orgSettings.website}</p>}
                    {orgSettings?.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> {orgSettings.email}</p>}
                    {orgSettings?.phone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> {orgSettings.phone}</p>}
                  </div>
                </div>
              </div>
              <div className="text-right space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40">Invoice</p>
                <p className="text-4xl font-bold tracking-tighter italic serif">{invoice.number}</p>
                <div className="pt-4 space-y-1">
                  <p className="text-xs font-bold">Issue Date: <span className="opacity-60">{new Date(invoice.date).toLocaleDateString()}</span></p>
                  <p className="text-xs font-bold">Due Date: <span className="opacity-60">{new Date(invoice.dueDate).toLocaleDateString()}</span></p>
                  {invoice.reference && <p className="text-xs font-bold">Reference: <span className="opacity-60">{invoice.reference}</span></p>}
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-[#141414]/5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Bill To</p>
              <p className="text-lg font-bold italic serif">{invoice.contactName || 'Valued Customer'}</p>
              <p className="text-xs text-[#141414]/60">{invoice.contactEmail}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#141414]/5 border-b border-[#141414]/10">
                  <th className="py-4 px-12 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest">Description</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Qty</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Unit Price</th>
                  <th className="py-4 px-4 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Disc</th>
                  <th className="py-4 px-12 text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#141414]/5">
                {invoice.items.map((item: any, i: number) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-6 px-12">
                      <p className="text-sm font-bold text-[#141414] italic serif">{item.description}</p>
                    </td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{item.quantity}</td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-6 px-4 text-sm font-bold text-[#141414] text-right">{(item.discount && item.discount > 0) ? ((item as any).discountType === 'amount' ? item.discount.toLocaleString() : `${item.discount}%`) : ''}</td>
                    <td className="py-6 px-12 text-sm font-bold text-[#141414] text-right">
                      {calculateItemTotal(item).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="p-12 flex flex-col items-end border-t border-[#141414]/5 bg-gray-50/30">
            <div className="w-full max-w-xs space-y-4">
              <div className="flex justify-between items-center text-xs font-bold text-[#141414]/50 uppercase tracking-widest">
                <span>Subtotal</span>
                <span className="text-[#141414]">{invoice.currency} {invoice.items.reduce((sum: number, item: any) => sum + calculateItemSubtotal(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-xs font-bold text-[#141414]/50 uppercase tracking-widest">
                <span>Tax</span>
                <span className="text-[#141414]">{invoice.currency} {invoice.items.reduce((sum: number, item: any) => sum + calculateItemTax(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="pt-6 mt-6 border-t border-[#141414]/10 flex justify-between items-center">
                <span className="text-xl font-bold uppercase tracking-tight text-[#141414] italic serif">Total</span>
                <div className="text-right">
                  <span className="text-3xl font-bold text-[#141414] tracking-tighter">{invoice.currency} {(invoice.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              {invoice.paymentReceived > 0 && (
                <div className="pt-4 border-t border-[#141414]/10 flex justify-between items-center">
                  <span className="text-sm font-bold uppercase tracking-tight text-[#141414] italic serif">Amount Paid</span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-[#141414] tracking-tighter">{invoice.currency} {(invoice.paymentReceived || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
              {invoice.paymentReceived > 0 && (
                <div className="pt-4 border-t border-[#141414]/10 flex justify-between items-center bg-[#141414]/5 p-2 rounded-lg">
                  <span className="text-sm font-bold uppercase tracking-tight text-[#141414] italic serif">Amount Due</span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-[#141414] tracking-tighter">{invoice.currency} {Math.max(0, invoice.totalAmount - (invoice.paymentReceived || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer / Terms */}
          {(invoice.termsAndConditions || invoice.paymentInstructions || brandingTheme?.invoiceFooterText) && (
            <div className="p-12 pt-0 border-t border-[#141414]/5 bg-gray-50/30">
              <div className="max-w-xl space-y-8">
                {invoice.paymentInstructions && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Payment Instructions</p>
                    <p className="text-xs text-[#141414]/70 leading-relaxed italic serif whitespace-pre-wrap">{invoice.paymentInstructions}</p>
                  </div>
                )}
                {invoice.termsAndConditions && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Terms & Conditions</p>
                    <p className="text-xs text-[#141414]/70 leading-relaxed italic serif whitespace-pre-wrap">{invoice.termsAndConditions}</p>
                  </div>
                )}
                {brandingTheme?.invoiceFooterText && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">Notes</p>
                    <p className="text-xs text-[#141414]/70 leading-relaxed italic serif whitespace-pre-wrap">{brandingTheme.invoiceFooterText}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="text-center space-y-4 opacity-40 py-12">
          <p className="text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2">
            Powered by Accountex Business Intelligence
          </p>
          <div className="flex justify-center gap-4 text-[10px]">
            <a href="#" className="hover:underline">Privacy Policy</a>
            <span>•</span>
            <a href="#" className="hover:underline">Terms of Service</a>
            <span>•</span>
            <a href="#" className="hover:underline">Contact Support</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
